

























































import { Prop, Component, Vue, Watch } from 'vue-property-decorator'
import { v4 } from 'uuid'

export type FetchItemCallback<T> = (itemId: string) => T
export type FindItemCallback<T> = (items: T[], itemId: string) => T
export type FindItemKeyCallback<T> = (item: T) => string
export type FindItemPositionCallback<T> = (item: T) => number
export type SetItemPositionCallback<T> = (item: T, position: number) => T

@Component
export default class ListContainer<T> extends Vue {
    uuid = 'list-container-' + v4()

    @Prop({ type: Boolean, default: false }) readonly isHorizontal: boolean
    @Prop({ type: Boolean, default: false }) readonly isNumbered: boolean
    @Prop({ type: Boolean, default: true }) readonly hasNumberReset: boolean
    @Prop({ type: Number, default: 0 }) readonly minimalItems: number
    @Prop({ type: Number, default: 0 }) readonly maxItems: number
    @Prop({ type: String, default: null }) readonly draggableContainerId!: string
    @Prop({ type: Array, default() { return [] }}) readonly items!: T[]
    @Prop({ type: Function }) readonly fetchItemCallback!: FetchItemCallback<T>
    @Prop({ type: Function, default(items, itemId) { return items.find(item => item.id === itemId) } }) readonly findItemCallback!: FindItemCallback<T>
    @Prop({ type: Function }) readonly findItemIdCallback!: FindItemKeyCallback<T>
    @Prop({ type: Function, default() { return 1 } }) readonly findItemPositionCallback!: FindItemPositionCallback<T>
    @Prop({ type: Function, default() { return null } }) readonly setItemPositionCallback!: SetItemPositionCallback<T>
    @Prop({ type: String, default: null }) readonly placeholderActionIcon!: string
    @Prop({ type: String, default: null }) readonly placeholderActionLabel!: string
    @Prop({ type: String, default: null }) readonly placeholderIcon!: string
    @Prop({ type: String, default: null }) readonly placeholderIconColor!: string
    @Prop({ type: Boolean, default: false }) readonly transitionDisabled!: boolean

    internalItems: T[] = []
    dragOver = false
    dragOverTimeout = null
    draggingOverCurrentContainer: boolean = false

    mounted() {
        this.internalItems = this.items
        this.$root.$on('list-item-moved', this.itemMoved)
    }

    @Watch('items')
    updateItems() {
        this.internalItems = this.items
    }

    @Watch('internalItems')
    emitUpdateEvent() {
        this.$emit('list-change', this.internalItems)
    }

    @Watch('$root.$dragItem')
    setDraggingOverCurrentContainer() {
        this.draggingOverCurrentContainer = this.$root.$dragItem.classList.contains(this.uuid)
    }

    get sortedInternalItems(): T[] {
        return [...this.internalItems].sort((a, b) => {
            const aSort = this.findItemPositionCallback(a)
            const bSort = this.findItemPositionCallback(b)

            if (aSort < bSort) {
                return -1
            }
            if (aSort > bSort) {
                return 1
            }

            return 0
        })
    }

    get minimalPlaceholders(): number {
        const minimalPlaceholders = this.maxItems - this.sortedInternalItems.length

        return minimalPlaceholders <= 0 ? 0 : minimalPlaceholders
    }

    get lastPosition(): number {
        if (this.sortedInternalItems.length === 0) {
            return 0
        }

        return this.findItemPositionCallback(this.sortedInternalItems[this.sortedInternalItems.length - 1])
    }

    get classes(): object {
        return {
            'list-container': true,
            'list-container--drop-container': this.isDragContainer,
            'list-container--numbered': this.isNumbered,
            'list-container--horizontal': this.isHorizontal,
            'list-container--has-number-reset': this.hasNumberReset,
        }
    }

    get variables(): object {
        return {
            '--list-container-counter-name': this.numberCounterName,
            '--list-container-counter-max-name': this.numberCounterName + '-max',
            '--list-container-placeholder-text': 'Fase',
        }
    }

    get numberCounterName(): string {
        return this.hasNumberReset ? 'section' : 'custom'
    }

    get isDragContainer() {
        return this.draggableContainerId !== null
    }

    get createPosition(): number {
        return this.sortedInternalItems.length + 1
    }

    itemMoved(event) {
        if (event.oldContainerId === event.newContainerId) {
            return
        }

        if (event.oldContainerId === this.uuid) {
            this.internalItems.splice(this.internalItems.findIndex(item => this.findItemIdCallback(item) === event.itemId), 1)
        }
    }

    assignPositions() {
        this.internalItems = [...this.sortedInternalItems].map((item, index) => {
            this.setItemPositionCallback(item, index + 1)
            return item
        })
    }

    dragoverHandler(event: DragEvent) {
        event.preventDefault()

        if (!this.isDragContainer) {
            return
        }

        event.dataTransfer.dropEffect = 'move'

        if (this.dragOverTimeout !== null) {
            clearTimeout(this.dragOverTimeout)
            this.dragOverTimeout = null
        }

        this.dragOver = true
        this.dragOverTimeout = setTimeout(() => {
            this.dragOver = false
            this.dragOverTimeout = null
        }, 100)
    }

    containerDropHandler(event: DragEvent, position: number) {
        const element = event.target as Element

        if (!element.matches('.list-container')) {
            return
        }

        this.dropHandler(event, position)
    }

    dropHandler(event: DragEvent, position: number) {
        event.preventDefault()

        if (!this.isDragContainer) {
            return
        }

        const draggableContainerId = event.dataTransfer.getData('draggableContainerId')
        const itemKey = event.dataTransfer.getData('itemKey')
        const containerId = event.dataTransfer.getData('containerId')

        if (draggableContainerId !== this.draggableContainerId) {
            return
        }

        let item = this.findItemCallback(this.internalItems, itemKey)

        if (!item) {
            item = this.fetchItemCallback(itemKey)
        }

        this.setItemPositionCallback(item, position)

        if (!this.internalItems.includes(item)) {
            this.internalItems.push(item)
        }

        this.assignPositions()

        this.$root.$emit('list-item-moved', {
            itemId: itemKey,
            oldContainerId: containerId,
            newContainerId: this.uuid,
        })
        this.$emit('list-item-moved', {
            itemId: itemKey,
            oldContainerId: containerId,
            newContainerId: this.uuid,
        })
    }
}
